import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import { ApiError, apiFetch } from "@/api/client";
import type { MyProfile, MySignature, UpdateMe, UpdateMySignature } from "@/api/schemas";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function ProfilePage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["my-signature"],
    queryFn: () => apiFetch<MySignature>("/api/users/me/signature"),
  });

  const form = useForm<{ signature_html: string; signature_enabled: boolean }>({
    defaultValues: { signature_html: "", signature_enabled: false },
  });

  useEffect(() => {
    if (data) {
      form.reset({
        signature_html: data.signature_html ?? "",
        signature_enabled: data.signature_enabled,
      });
    }
  }, [data, form]);

  const mutation = useMutation({
    mutationFn: (body: UpdateMySignature) =>
      apiFetch<MySignature>("/api/users/me/signature", { method: "PUT", json: body }),
    onSuccess: () => {
      toast.success("Signature saved");
      void qc.invalidateQueries({ queryKey: ["my-signature"] });
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : "Could not save signature"),
  });

  return (
    <div className="mx-auto max-w-2xl space-y-6 px-6 py-8">
      <div>
        <h1 className="text-xl font-medium tracking-tight">Profile</h1>
        <p className="mt-1 text-sm text-text-muted">
          Personal settings that apply across every mailbox you work in.
        </p>
      </div>

      <section className="rounded-lg border border-border bg-surface p-5">
        <h2 className="mb-1 text-sm font-medium tracking-tight">Personal signature</h2>
        <p className="mb-4 text-xs text-text-muted">
          Appended below the mailbox signature when you reply, if "Send my
          signature" is on. Each reply also has a per-message toggle in the
          composer.
        </p>
        {isLoading ? (
          <p className="text-sm text-text-muted">Loading…</p>
        ) : (
          <form
            onSubmit={form.handleSubmit((v) =>
              mutation.mutate({
                signature_html: v.signature_html.trim() || null,
                signature_enabled: v.signature_enabled,
              }),
            )}
            className="flex flex-col gap-4"
          >
            <div className="flex flex-col gap-1.5">
              <Label>Signature HTML</Label>
              <textarea
                {...form.register("signature_html", { maxLength: 300_000 })}
                rows={5}
                className="flex w-full rounded border border-border bg-surface px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                placeholder="<p>Pavle Smakic<br/>Senior Support — Acme</p>"
              />
              <p className="text-xs text-text-muted">
                HTML allowed (links, bold, line breaks). Your tenant
                administrator may configure a tighter sanitiser later.
              </p>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                {...form.register("signature_enabled")}
                className="h-4 w-4 rounded border-border"
              />
              <span>Send my signature with replies</span>
            </label>
            <div>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Save
              </Button>
            </div>
          </form>
        )}
      </section>

      <ChangePasswordSection />
    </div>
  );
}

type PasswordForm = {
  current_password: string;
  new_password: string;
  confirm_password: string;
};

function ChangePasswordSection() {
  const form = useForm<PasswordForm>({
    defaultValues: {
      current_password: "",
      new_password: "",
      confirm_password: "",
    },
  });

  const mutation = useMutation({
    mutationFn: (body: UpdateMe) =>
      apiFetch<MyProfile>("/api/users/me", { method: "PUT", json: body }),
    onSuccess: () => {
      toast.success("Password updated");
      form.reset();
    },
    onError: (e) => {
      if (e instanceof ApiError) {
        if (e.code === "invalid_credentials") {
          form.setError("current_password", {
            type: "server",
            message: "Current password is incorrect",
          });
          return;
        }
        if (e.code === "validation_failed") {
          form.setError("new_password", { type: "server", message: e.message });
          return;
        }
      }
      toast.error(e instanceof ApiError ? e.message : "Could not update password");
    },
  });

  const errors = form.formState.errors;

  return (
    <section className="rounded-lg border border-border bg-surface p-5">
      <h2 className="mb-1 text-sm font-medium tracking-tight">Change password</h2>
      <p className="mb-4 text-xs text-text-muted">
        Updates your login password. You'll stay signed in on this device.
      </p>
      <form
        onSubmit={form.handleSubmit((v) =>
          mutation.mutate({
            current_password: v.current_password,
            new_password: v.new_password,
          }),
        )}
        className="flex flex-col gap-4"
      >
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="current_password">Current password</Label>
          <Input
            id="current_password"
            type="password"
            autoComplete="current-password"
            aria-invalid={errors.current_password ? true : undefined}
            {...form.register("current_password", { required: "Required" })}
          />
          {errors.current_password && (
            <p className="text-xs text-danger">{errors.current_password.message}</p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="new_password">New password</Label>
          <Input
            id="new_password"
            type="password"
            autoComplete="new-password"
            aria-invalid={errors.new_password ? true : undefined}
            {...form.register("new_password", {
              required: "Required",
              minLength: { value: 8, message: "Must be at least 8 characters" },
            })}
          />
          {errors.new_password ? (
            <p className="text-xs text-danger">{errors.new_password.message}</p>
          ) : (
            <p className="text-xs text-text-muted">Minimum 8 characters.</p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="confirm_password">Confirm new password</Label>
          <Input
            id="confirm_password"
            type="password"
            autoComplete="new-password"
            aria-invalid={errors.confirm_password ? true : undefined}
            {...form.register("confirm_password", {
              required: "Required",
              validate: (v, values) =>
                v === values.new_password || "Passwords do not match",
            })}
          />
          {errors.confirm_password && (
            <p className="text-xs text-danger">{errors.confirm_password.message}</p>
          )}
        </div>

        <div>
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Update password
          </Button>
        </div>
      </form>
    </section>
  );
}

export const Route = createFileRoute("/_auth/settings/profile")({
  component: ProfilePage,
});
